import { Link, useParams } from "react-router-dom";
import { getPostById, posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import Breadcrumb from "@/components/base/Breadcrumb";
import PostCard, { formatViews } from "@/components/feature/PostCard";
import JsonLd from "@/components/feature/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const post = id ? getPostById(id) : undefined;

  usePageMeta({
    title: post ? `${post.title} - 黑料每日` : "内容未找到 - 黑料每日",
    description: post?.summary,
    canonical: post ? `https://ranqiwangyou.cn/post/${post.id}` : undefined,
  });

  if (!post) {
    return (
      <section className="w-full px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">
          这条内容可能已经被归档更新
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的帖子不存在或链接已变更，去汇总清单里找找看吧。
        </p>
        <Link
          to="/summary"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-primary-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-list-check-2"></i>
          返回汇总清单
        </Link>
      </section>
    );
  }

  const category = categories.find((c) => c.id === post.categoryId);
  const related = posts
    .filter((p) => p.id !== post.id && p.categoryId === post.categoryId)
    .slice(0, 3);
  const fallbackRelated = posts
    .filter((p) => p.id !== post.id && p.categoryId !== post.categoryId)
    .slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.summary,
          datePublished: post.date,
          dateModified: post.date,
          inLanguage: "zh-CN",
          image: post.cover,
          keywords: post.tags.join(","),
          author: { "@type": "Organization", name: "黑料每日" },
          publisher: { "@type": "Organization", name: "黑料每日" },
          articleSection: category?.name,
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://ranqiwangyou.cn/" },
            { "@type": "ListItem", position: 2, name: "汇总清单", item: "https://ranqiwangyou.cn/summary" },
            { "@type": "ListItem", position: 3, name: post.title },
          ],
        }}
      />

      <article className="w-full">
        <div className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb
              items={[
                { label: "汇总清单", to: "/summary" },
                { label: category?.name ?? "资讯", to: `/summary?cat=${post.categoryId}` },
                { label: post.title },
              ]}
            />
            <div className="mt-5 max-w-4xl">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <Link
                  to={`/summary?cat=${post.categoryId}`}
                  className="cursor-pointer rounded-md bg-primary-600 px-2.5 py-1 font-medium text-primary-50 transition-colors hover:bg-primary-700"
                >
                  {category?.name}
                </Link>
                <span className="text-foreground-500">{post.date}</span>
                <span className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {formatViews(post.views)} 阅读
                </span>
                {post.hot && (
                  <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-1 font-medium text-accent-800">
                    <i className="ri-fire-line"></i>
                    热议中
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
                {post.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {post.summary}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-xl bg-background-200 md:h-[420px]">
                <img
                  src={post.cover}
                  alt={`${post.title}配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <p className="mt-3 text-center text-xs text-foreground-500">
                配图为示意图，仅用于辅助阅读氛围
              </p>

              <div className="mt-8 space-y-5">
                {post.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-base leading-loose text-foreground-800"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm font-medium text-foreground-700">
                  相关标签：
                </span>
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/summary?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-accent-900">
                  <i className="ri-information-line"></i>
                  阅读提醒
                </p>
                <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
                  本文为热点整理内容，涉及主体多采用泛化表述。若信息尚未得到官方确认，请以理性态度看待，不要传播未经核实的细节。
                </p>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  相关内容推荐
                </h2>
                <div className="mt-4 space-y-3">
                  {relatedList.map((item) => (
                    <PostCard key={item.id} post={item} variant="compact" />
                  ))}
                </div>
                <Link
                  to={`/summary?cat=${post.categoryId}`}
                  className="mt-5 flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  查看「{category?.name}」全部清单
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}